import { DEFAULT_TEMPLATE_ID, FullCVWithTemplate, TemplateId } from '@cibli/shared';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { getTemplate } from '../templates/registry';
import TemplateHost from '../templates/TemplateHost';

function decodeBase64UrlUtf8(value: string): string {
  const base64 = value.replaceAll('-', '+').replaceAll('_', '/');
  const padding = (4 - (base64.length % 4)) % 4;
  const padded = `${base64}${'='.repeat(padding)}`;
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function CVPayloadPreviewPage() {
  const [searchParams] = useSearchParams();
  const hashData = useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    return new URLSearchParams(hash).get('data');
  }, []);
  const rawData = hashData ?? searchParams.get('data');

  const cv = useMemo<FullCVWithTemplate | null>(() => {
    if (!rawData) {
      return null;
    }

    try {
      const decoded = decodeBase64UrlUtf8(rawData);
      const parsed = JSON.parse(decoded) as FullCVWithTemplate;
      return parsed;
    } catch (error) {
      console.error('Failed to decode payload preview data', error);
      return null;
    }
  }, [rawData]);

  const templateId = useMemo(
    () => (cv?.template || DEFAULT_TEMPLATE_ID) as TemplateId,
    [cv?.template],
  );
  const template = useMemo(() => getTemplate(templateId), [templateId]);
  const initialParams = useMemo(
    () => cv?.templateParams || template.defaults,
    [cv?.templateParams, template.defaults],
  );
  const params = useMemo(() => {
    const parsed = template.schema.safeParse(initialParams);
    return parsed.success ? parsed.data : template.defaults;
  }, [initialParams, template]);

  if (!cv) {
    return <div className="text-xl text-red-500">Payload CV invalide</div>;
  }

  return (
    <div className="w-full flex justify-center print:block print:w-auto print:m-0 print:p-0">
      <div className="w-[210mm] print:w-[210mm]">
        <TemplateHost cv={cv} mode="print" templateId={templateId} initialParams={params} />
      </div>
    </div>
  );
}
